package com.essntl.features.proposal.data.supabase.proposalservice

import com.essntl.features.proposal.domain.model.ProposalServiceModel
import core.config.CurrencyConfig
import kotlinx.serialization.json.*
import kotlin.math.max

internal fun ProposalServiceModel.toRequestMap(ignoreNullFields: Boolean = true): Map<String, JsonElement> {
    val requestMap = mutableMapOf<String, JsonElement>()

    requestMap["proposal_id"] = JsonPrimitive(proposalId)

    requestMap["service_id"] = JsonPrimitive(service.id)

    if (archived != null || !ignoreNullFields) {
        requestMap["archived"] = JsonPrimitive(archived ?: false)
    }

    if (grouped != null || !ignoreNullFields) {
        requestMap["grouped"] = JsonPrimitive(grouped ?: false)
    }

    if (commissionable != null || !ignoreNullFields) {
        requestMap["commissionable"] = JsonPrimitive(commissionable ?: true)
    }

    if (bookedByTa != null || !ignoreNullFields) {
        requestMap["booked_by_ta"] = JsonPrimitive(bookedByTa ?: false)
    }

    if (dynamicPriceEnabled != null || !ignoreNullFields) {
        requestMap["dynamic_price_enabled"] = JsonPrimitive(dynamicPriceEnabled ?: false)
    }

    if (additionalInfo != null || !ignoreNullFields) {
        requestMap["additional_info"] = JsonPrimitive(additionalInfo ?: "")
    }

    if (editedDescription != null || !ignoreNullFields) {
        requestMap["edited_description"] = JsonPrimitive(editedDescription ?: "")
    }

    if (startDate.isNotEmpty() || !ignoreNullFields) {
        requestMap["start_date"] = JsonPrimitive(startDate)
    }

    if (endDate != null || !ignoreNullFields) {
        requestMap["end_date"] = JsonPrimitive(endDate.orEmpty().ifBlank { startDate.ifBlank { null } })
    }

    if (startTime != null || !ignoreNullFields) {
        requestMap["start_time"] = JsonPrimitive(startTime?.ifBlank { null })
    }

    if (endTime != null || !ignoreNullFields) {
        requestMap["end_time"] = JsonPrimitive(endTime?.ifBlank { null })
    }

    if (index != -1 || !ignoreNullFields) {
        requestMap["index"] = JsonPrimitive(index)
    }

    if (price != null || !ignoreNullFields) {
        requestMap["price"] = JsonPrimitive(price)
        requestMap["price_currency"] = JsonPrimitive(priceCurrency?.code ?: CurrencyConfig.defaultCurrencyCode)
    }

    if (cost != null || !ignoreNullFields) {
        requestMap["cost"] = JsonPrimitive(cost)
        requestMap["cost_currency"] = JsonPrimitive(priceCurrency?.code ?: CurrencyConfig.defaultCurrencyCode)
    }

    if (internalNotes != null || !ignoreNullFields) {
        requestMap["internal_notes"] = JsonPrimitive(internalNotes)
    }

    if (status != null) {
        requestMap["status"] = JsonPrimitive(status?.toDto())
    }

    if (supplier != null || !ignoreNullFields) {
        requestMap["supplier_id"] = JsonPrimitive(supplier?.id)
    }

    if (guestCount != -1 || !ignoreNullFields) {
        requestMap["guest_count"] = JsonPrimitive(max(1, guestCount))
    }

    if (dynamicFields != null || !ignoreNullFields) {
        requestMap["dynamic_fields"] = Json.encodeToJsonElement(dynamicFields)
    }

    if (itineraryId != null || !ignoreNullFields) {
        requestMap["itinerary_id"] = JsonPrimitive(itineraryId)
    }

    if (specialNotes != null || !ignoreNullFields) {
        requestMap["special_notes"] = JsonPrimitive(specialNotes)
    }

    if (paid != null || !ignoreNullFields) {
        requestMap["paid"] = JsonPrimitive(paid ?: false)
    }

    if (embeddedMap != null || !ignoreNullFields) {
        requestMap["embedded_map"] = JsonPrimitive(embeddedMap ?: "")
    }

    if (embeddedContent != null || !ignoreNullFields) {
        requestMap["embedded_content"] = JsonPrimitive(embeddedContent ?: "")
    }

    if (files != null || !ignoreNullFields) {
        requestMap["files"] = JsonArray(files?.map { JsonPrimitive(it) }.orEmpty())
    }

    if (priceCurrency != null || !ignoreNullFields) {
        requestMap["price_currency"] = JsonPrimitive(priceCurrency?.code)
    }

    if (costCurrency != null || !ignoreNullFields) {
        requestMap["cost_currency"] = JsonPrimitive(costCurrency?.code)
    }

    hideOnProposal?.let { requestMap["hide_on_proposal"] = JsonPrimitive(hideOnProposal) }

    return requestMap
}